import React, { useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import { SearchSuggestionViewModel } from "../../api/generated/models/SearchSuggestionViewModel";
import { BusinessCategory } from "../../api/businessCategories";
import { Button } from "../Button";
import { Loader } from "../Loader";
import { normalizeKeyword, normalizeLogoUrl } from "../../helpers/searchSuggestions";
import {
  searchBusinesses,
  TypesenseBusinessDocument,
} from "../../utils/typesenseBusinessSearch";

export interface SearchSuggestionDraft {
  keywords: string[];
  businessIds: string[];
  categoryIds: string[];
  isPublished: boolean;
  note: string;
}

const MAX_KEYWORD_LENGTH = 64;

const emptyDraft = (presetKeyword?: string): SearchSuggestionDraft => ({
  keywords: presetKeyword ? [normalizeKeyword(presetKeyword)] : [],
  businessIds: [],
  categoryIds: [],
  isPublished: true,
  note: "",
});

const draftFrom = (suggestion: SearchSuggestionViewModel): SearchSuggestionDraft => ({
  keywords: suggestion.keywords ?? [],
  businessIds: suggestion.businessIds ?? [],
  categoryIds: suggestion.categoryIds ?? [],
  isPublished: suggestion.isPublished ?? false,
  note: suggestion.note ?? "",
});

const MerchantRow: React.FC<{
  business: TypesenseBusinessDocument;
  action: React.ReactNode;
}> = ({ business, action }) => {
  const logoUrl = normalizeLogoUrl(business.logo_url);
  return (
    <div className="flex items-center gap-3 p-2 rounded-lg">
      {logoUrl ? (
        <img
          src={logoUrl}
          alt={business.business_name}
          className="w-8 h-8 rounded-lg object-cover border border-gray-200 flex-shrink-0"
        />
      ) : (
        <div className="w-8 h-8 rounded-lg bg-gray-200 flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm text-gray-900 truncate">
          {business.business_name}
        </p>
        <p className="text-xs text-gray-400">
          {[business.cashback_display, business.partner_type].filter(Boolean).join(" · ")}
        </p>
      </div>
      {action}
    </div>
  );
};

/**
 * One modal for both create and edit. Merchants are picked from the same
 * Typesense collection the web app searches, so a picked merchant is by
 * definition one the app can render. A whole category can be added instead
 * of, or on top of, single merchants: the backend expands it into the
 * category's active merchants when the suggestion is served.
 */
const SearchSuggestionEditor: React.FC<{
  isOpen: boolean;
  suggestion: SearchSuggestionViewModel | null;
  presetKeyword?: string;
  knownBusinesses: Map<string, TypesenseBusinessDocument>;
  categories: BusinessCategory[];
  onClose: () => void;
  onSave: (draft: SearchSuggestionDraft) => Promise<void>;
}> = ({ isOpen, suggestion, presetKeyword, knownBusinesses, categories, onClose, onSave }) => {
  const [draft, setDraft] = useState<SearchSuggestionDraft>(emptyDraft());
  const [keywordInput, setKeywordInput] = useState("");
  const [businesses, setBusinesses] = useState<Map<string, TypesenseBusinessDocument>>(
    new Map()
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<TypesenseBusinessDocument[]>([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setDraft(suggestion ? draftFrom(suggestion) : emptyDraft(presetKeyword));
    setBusinesses(new Map(knownBusinesses));
    setKeywordInput("");
    setSearchQuery("");
    setSearchResults([]);
    setError(null);
  }, [isOpen, suggestion, presetKeyword, knownBusinesses]);

  useEffect(() => {
    if (!isOpen) return;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (searchQuery.trim() === "") {
      setSearchResults([]);
      return;
    }
    searchTimerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { results } = await searchBusinesses(searchQuery, 1, 20);
        setSearchResults(results);
      } catch (err) {
        console.error("Error searching businesses:", err);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchQuery, isOpen]);

  const addKeyword = () => {
    const keyword = normalizeKeyword(keywordInput);
    setKeywordInput("");
    if (!keyword || keyword.length > MAX_KEYWORD_LENGTH) return;
    setDraft((prev) =>
      prev.keywords.includes(keyword)
        ? prev
        : { ...prev, keywords: [...prev.keywords, keyword] }
    );
  };

  const removeKeyword = (keyword: string) =>
    setDraft((prev) => ({
      ...prev,
      keywords: prev.keywords.filter((k) => k !== keyword),
    }));

  const addBusiness = (business: TypesenseBusinessDocument) => {
    setBusinesses((prev) => new Map(prev).set(business.id, business));
    setDraft((prev) =>
      prev.businessIds.includes(business.id)
        ? prev
        : { ...prev, businessIds: [...prev.businessIds, business.id] }
    );
    setSearchQuery("");
    setSearchResults([]);
  };

  const removeBusiness = (id: string) =>
    setDraft((prev) => ({
      ...prev,
      businessIds: prev.businessIds.filter((b) => b !== id),
    }));

  const addCategory = (id: string) => {
    if (!id) return;
    setDraft((prev) =>
      prev.categoryIds.includes(id)
        ? prev
        : { ...prev, categoryIds: [...prev.categoryIds, id] }
    );
  };

  const removeCategory = (id: string) =>
    setDraft((prev) => ({
      ...prev,
      categoryIds: prev.categoryIds.filter((c) => c !== id),
    }));

  const handleSave = async () => {
    // A keyword typed but not confirmed with Enter is still meant to be saved.
    const pending = normalizeKeyword(keywordInput);
    const keywords =
      pending && !draft.keywords.includes(pending)
        ? [...draft.keywords, pending]
        : draft.keywords;
    if (keywords.length === 0) {
      setError("Add at least one keyword.");
      return;
    }
    if (draft.businessIds.length === 0 && draft.categoryIds.length === 0) {
      setError("Add at least one merchant or category.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave({ ...draft, keywords });
    } catch (err: any) {
      setError(err?.body?.message || err?.message || "Could not save the suggestion.");
    } finally {
      setSaving(false);
    }
  };

  const selectedIds = new Set(draft.businessIds);
  const candidates = searchResults.filter((r) => !selectedIds.has(r.id));
  const categoryName = (id: string) => categories.find((c) => c.id === id)?.name ?? id;
  const availableCategories = categories.filter((c) => !draft.categoryIds.includes(c.id));

  return (
    <Modal
      className="modal-content md:max-w-[640px]"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel={suggestion ? "Edit suggestion" : "New suggestion"}
    >
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-primary font-medium text-xl">
          {suggestion ? "Edit suggestion" : "New suggestion"}
        </h2>
        <Button variant="icon" onClick={onClose} disabled={saving}>
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </Button>
      </div>

      <label className="block text-sm font-medium text-gray-700 mb-1">Keywords</label>
      <p className="text-xs text-gray-400 mb-2">
        Exact matches only, case-insensitive. Add spelling variants as separate
        keywords. Press Enter or comma to add.
      </p>
      <div className="flex flex-wrap gap-1 mb-2">
        {draft.keywords.map((keyword) => (
          <span
            key={keyword}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs"
          >
            {keyword}
            <button
              type="button"
              onClick={() => removeKeyword(keyword)}
              aria-label={`Remove keyword ${keyword}`}
              className="hover:text-red-500"
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <input
        type="text"
        value={keywordInput}
        onChange={(e) => setKeywordInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            addKeyword();
          }
        }}
        onBlur={addKeyword}
        maxLength={MAX_KEYWORD_LENGTH}
        placeholder="e.g. boozt"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent mb-4"
      />

      <label className="block text-sm font-medium text-gray-700 mb-1">Merchants</label>
      <p className="text-xs text-gray-400 mb-2">Shown in this order.</p>
      {draft.businessIds.length > 0 ? (
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 mb-2">
          {draft.businessIds.map((id) => {
            const business = businesses.get(id);
            const remove = (
              <button
                type="button"
                onClick={() => removeBusiness(id)}
                className="text-xs text-red-500 hover:underline"
              >
                Remove
              </button>
            );
            return business ? (
              <MerchantRow key={id} business={business} action={remove} />
            ) : (
              <div key={id} className="flex items-center justify-between p-2">
                <span className="text-xs italic text-gray-400" title={id}>
                  not in search index
                </span>
                {remove}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-gray-400 italic mb-2">No merchants added yet.</p>
      )}
      <div className="relative mb-4">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search merchants to add..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        />
        {searchQuery.trim() !== "" && (
          <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
            {searching ? (
              <div className="flex justify-center py-4">
                <Loader />
              </div>
            ) : candidates.length === 0 ? (
              <p className="text-sm text-gray-400 p-3">No merchants found.</p>
            ) : (
              candidates.map((business) => (
                <button
                  key={business.id}
                  type="button"
                  onClick={() => addBusiness(business)}
                  className="w-full text-left hover:bg-gray-50"
                >
                  <MerchantRow business={business} action={null} />
                </button>
              ))
            )}
          </div>
        )}
      </div>

      <label htmlFor="suggestion-category" className="block text-sm font-medium text-gray-700 mb-1">
        Categories
      </label>
      <p className="text-xs text-gray-400 mb-2">
        Every active merchant in the category, listed after the merchants above.
        At most 12 merchants are shown in total.
      </p>
      <div className="flex flex-wrap gap-1 mb-2">
        {draft.categoryIds.map((id) => (
          <span
            key={id}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs"
          >
            {categoryName(id)}
            <button
              type="button"
              onClick={() => removeCategory(id)}
              aria-label={`Remove category ${categoryName(id)}`}
              className="hover:text-red-500"
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <select
        id="suggestion-category"
        value=""
        onChange={(e) => addCategory(e.target.value)}
        disabled={availableCategories.length === 0}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent mb-4 bg-white"
      >
        <option value="">
          {categories.length === 0 ? "Categories unavailable" : "Add a category..."}
        </option>
        {availableCategories.map((category) => (
          <option key={category.id} value={category.id}>
            {category.name}
          </option>
        ))}
      </select>

      <label className="flex items-center gap-2 text-sm text-gray-700 mb-4">
        <input
          type="checkbox"
          checked={draft.isPublished}
          onChange={(e) => setDraft((prev) => ({ ...prev, isPublished: e.target.checked }))}
        />
        Published
      </label>

      <label className="block text-sm font-medium text-gray-700 mb-1">Note (internal)</label>
      <textarea
        value={draft.note}
        onChange={(e) => setDraft((prev) => ({ ...prev, note: e.target.value }))}
        rows={2}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent mb-4"
      />

      {error && <p className="text-sm text-red-500 mb-4">{error}</p>}

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} isLoading={saving}>
          Save
        </Button>
      </div>
    </Modal>
  );
};

export default SearchSuggestionEditor;
